/**
 * 채팅 전송 — 백엔드 `/api/v1/chat/stream` 단일 엔드포인트.
 *
 * 새 메시지·후속 메시지·HITL 재개가 **한 흐름**이다(백엔드 `ChatRequest` 계약). 프런트가
 * 엔드포인트를 나누면 그 순간 계약이 둘이 된다.
 */
import { request, requestStream } from '@/api/client'
import { API } from '@/api/paths'
import type { RunEvent } from './events'
import { parseSseStream } from './sse'

export interface SendOptions {
  message: string
  threadId?: string | null
  fileRefs?: string[]
  /**
   * 이 대화를 시작한 프로젝트(F-6). **첫 턴에만 의미가 있다** — 백엔드가 소유를 확인한 뒤
   * 그 소속으로 귀속하고 지침을 싣는다. 이미 소속이 있는 thread 로 보내면 무시된다
   * (소속 변경은 `setThreadProject` 의 일이다).
   */
  projectId?: string | null
  signal?: AbortSignal
}

/**
 * 게이트 응답 — **비대칭 계약**(F-0 §1.1 L2 이관분).
 *
 * 승인은 스칼라 `true`, 반려는 객체 `{approved:false, reason}` 다. 대칭으로 만들면 백엔드가
 * 못 알아듣는다. 이 모양을 만드는 곳을 여기 하나로 두는 이유가 그것이다 — 호출부마다 조립하면
 * 그중 하나가 반드시 어긋나고, 어긋나는 대상이 하필 유일한 비가역 조작(발주)이다.
 */
export type GateDecision = { approved: true } | { approved: false; reason: string }

export function resumePayload(decision: GateDecision): true | { approved: false; reason: string } {
  return decision.approved ? true : { approved: false, reason: decision.reason }
}

async function* streamEvents(response: Response): AsyncGenerator<RunEvent> {
  if (!response.body) throw new Error('스트림 본문이 없습니다.')
  yield* parseSseStream(response.body)
}

/** 새/후속 메시지 전송 → 이벤트 스트림. */
export async function* sendMessage(options: SendOptions): AsyncGenerator<RunEvent> {
  const body: Record<string, unknown> = { message: options.message }
  if (options.threadId) body.thread_id = options.threadId
  if (options.fileRefs?.length) body.file_refs = options.fileRefs
  if (options.projectId) body.project_id = options.projectId
  yield* streamEvents(await requestStream(API.chatStream, body, options.signal))
}

/** 게이트 재개 → 이벤트 스트림. `message` 는 싣지 않는다(백엔드가 요구하지 않는다). */
export async function* resumeGate(
  threadId: string,
  decision: GateDecision,
  signal?: AbortSignal,
): AsyncGenerator<RunEvent> {
  const body = { thread_id: threadId, resume: resumePayload(decision) }
  yield* streamEvents(await requestStream(API.chatStream, body, signal))
}

export interface UploadedFile {
  file_ref: string
  filename: string
  size: number
  content_type: string
}

/** 업로드 → file_ref. 바이트가 아니라 **ref 만** 대화에 실린다(백엔드 D5). */
export async function uploadFile(file: File): Promise<UploadedFile> {
  const form = new FormData()
  form.append('file', file, file.name)
  return await request<UploadedFile>(API.uploads, { method: 'POST', body: form })
}
